import React, { useState, useEffect } from 'react';
import './CookiePolicy.css';

function CookiePolicy() {
  const [acceptedCookies, setAcceptedCookies] = useState(false);

  useEffect(() => {
    // Check if user has already accepted cookies
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (cookieConsent) {
      setAcceptedCookies(true);
    }
  }, []);

  const handleAcceptCookies = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setAcceptedCookies(true);
  };

  const handleRejectCookies = () => {
    localStorage.setItem('cookieConsent', 'rejected');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
  };

  return (
    <div className="policy-container cookie-policy">
      <div className="policy-header">
        <h1>🍪 Cookie Policy</h1>
        <p className="last-updated">Last Updated: January 8, 2026</p>
      </div>

      <div className="policy-content">
        <section className="policy-section">
          <h2>1. What Are Cookies?</h2>
          <p>
            Cookies are small text files that are placed on your device (computer, tablet, or mobile phone) 
            when you visit our website. These files allow us to recognize your device and enhance your browsing experience.
          </p>
        </section>

        <section className="policy-section">
          <h2>2. Types of Cookies We Use</h2>
          
          <div className="cookie-type">
            <h3>2.1 Essential Cookies</h3>
            <p>
              These cookies are necessary for the proper functioning of our website. They enable you to:
            </p>
            <ul>
              <li>Navigate through our website</li>
              <li>Access secure areas</li>
              <li>Remember your login information</li>
              <li>Complete transactions</li>
              <li>Maintain your shopping cart</li>
            </ul>
            <p><strong>Duration:</strong> Session-based or up to 12 months</p>
          </div>

          <div className="cookie-type">
            <h3>2.2 Performance Cookies</h3>
            <p>
              These cookies help us understand how visitors interact with our website by:
            </p>
            <ul>
              <li>Collecting information about pages visited</li>
              <li>Recording how long you spend on a page</li>
              <li>Tracking click patterns</li>
              <li>Identifying any errors that occur</li>
              <li>Measuring website performance</li>
            </ul>
            <p><strong>Duration:</strong> Up to 2 years</p>
          </div>

          <div className="cookie-type">
            <h3>2.3 Functional Cookies</h3>
            <p>
              These cookies allow our website to remember choices you make, including:
            </p>
            <ul>
              <li>Your language preference</li>
              <li>Your location</li>
              <li>Your username</li>
              <li>Items saved in your favorites</li>
              <li>Website personalization settings</li>
            </ul>
            <p><strong>Duration:</strong> Up to 12 months</p>
          </div>

          <div className="cookie-type">
            <h3>2.4 Targeting/Advertising Cookies</h3>
            <p>
              These cookies are used to deliver targeted advertisements based on your interests and behavior:
            </p>
            <ul>
              <li>Track your browsing patterns</li>
              <li>Create user profiles for marketing</li>
              <li>Display relevant product recommendations</li>
              <li>Measure advertising campaign effectiveness</li>
            </ul>
            <p><strong>Duration:</strong> Up to 2 years</p>
          </div>
        </section>

        <section className="policy-section">
          <h2>3. Third-Party Cookies</h2>
          <p>
            We may allow third parties to place cookies on your device for the purposes of:
          </p>
          <ul>
            <li><strong>Analytics:</strong> Google Analytics to understand website usage</li>
            <li><strong>Payment Processing:</strong> Stripe and PayPal for secure transactions</li>
            <li><strong>Social Media:</strong> Facebook and Instagram pixels for marketing</li>
            <li><strong>Advertising:</strong> Google Ads and other ad networks</li>
          </ul>
          <p>
            These third parties have their own privacy policies and we recommend reviewing them.
          </p>
        </section>

        <section className="policy-section">
          <h2>4. How to Control Cookies</h2>
          <p>
            Most web browsers allow you to control cookies through their settings. You can:
          </p>
          <ul>
            <li>Accept or reject all cookies</li>
            <li>Accept only specific types of cookies</li>
            <li>Delete existing cookies</li>
            <li>Set your browser to notify you when a cookie is being set</li>
          </ul>
          <p>
            <strong>Note:</strong> Disabling essential cookies may affect the functionality of our website, 
            including your ability to log in, make purchases, or access certain features.
          </p>
          <p>
            <strong>Browser Instructions:</strong>
          </p>
          <ul>
            <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies and other site data</li>
            <li><strong>Firefox:</strong> Preferences → Privacy & Security → Cookies and Site Data</li>
            <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
            <li><strong>Edge:</strong> Settings → Privacy, search, and services → Cookies and other site data</li>
            <li><strong>Internet Explorer:</strong> Tools → Internet Options → General Tab → Browsing history</li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>5. Cookies and Your Privacy</h2>
          <p>
            The information collected through cookies is subject to our Privacy Policy. 
            We do not use cookies to store sensitive personal information such as:
          </p>
          <ul>
            <li>Credit card or banking details</li>
            <li>Passwords</li>
            <li>Social security or ID numbers</li>
            <li>Health information</li>
          </ul>
          <p>
            For detailed information about how we use your personal data, please refer to our 
            <a href="/privacy-policy"> Privacy Policy</a>.
          </p>
        </section>

        <section className="policy-section">
          <h2>6. Do Not Track (DNT) Signals</h2>
          <p>
            Some browsers include a "Do Not Track" (DNT) feature. Currently, there is no industry-wide standard 
            for how websites should respond to DNT signals. Our website does not currently respond to DNT signals, 
            but we recommend configuring your browser privacy settings to align with your preferences.
          </p>
        </section>

        <section className="policy-section">
          <h2>7. Changes to This Cookie Policy</h2>
          <p>
            We may update this Cookie Policy from time to time to reflect changes in our practices, 
            technology, legal requirements, or other factors. We will notify you by updating the 
            "Last Updated" date at the top of this policy.
          </p>
          <p>
            Your continued use of our website following the posting of changes constitutes your acceptance 
            of any such changes.
          </p>
        </section>

        <section className="policy-section">
          <h2>8. Contact Us</h2>
          <p>
            If you have any questions or concerns about our use of cookies, please contact us at:
          </p>
          <div className="contact-info">
            <p><strong>Email:</strong> <a href="mailto:mashaba.enterprise2025@gmail.com">mashaba.enterprise2025@gmail.com</a></p>
            <div className="website-logo">
              <img src="/logo.png" alt="Heritage Hub Logo" className="logo-image" />
            </div>
          </div>
        </section>

        <section className="cookie-banner">
          <div className="banner-content">
            <h3>Cookie Consent</h3>
            <p>
              We use cookies to enhance your browsing experience and analyze website traffic. 
              By using our website, you consent to our use of cookies in accordance with this policy.
            </p>
            <div className="banner-actions">
              <button onClick={handleAcceptCookies} className="accept-btn">
                Accept All Cookies
              </button>
              <button onClick={handleRejectCookies} className="reject-btn">
                Reject Non-Essential Cookies
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default CookiePolicy;
